import React, { useEffect, useState, useRef, Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, MessageSquare } from 'lucide-react';
import { useTheme } from '../../../core/contexts/ThemeContext';
import { useWindows } from '../../../core/contexts/WindowContext';
import { projects, Project } from './data/projects';
export function WorkApp({
  projectId
}: {
  projectId?: string;
}) {
  const {
    openWindow
  } = useWindows();
  const {
    theme,
    language,
    t
  } = useTheme();
  const isLightTheme = theme === 'light';
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedProject, setSelectedProject] = useState<Project | null>(projectId ? projects.find(p => p.id === projectId) || null : null);
  const filters = [{
    id: 'all',
    label: t('work.filters.all')
  }, {
    id: 'apps',
    label: t('work.filters.apps')
  }, {
    id: 'video',
    label: t('work.filters.video')
  }];
  const filteredProjects = activeFilter === 'all' ? projects : projects.filter(p => p.category === activeFilter);
  const flagship = filteredProjects[0];
  const restProjects = filteredProjects.slice(1);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, [selectedProject]);
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 200,
        damping: 28
      }
    }
  };
  if (selectedProject) {
    const projectStory = language === 'en' ? selectedProject.storyEn : selectedProject.story;
    return <div ref={scrollContainerRef} className="h-full overflow-y-auto pb-28" data-id="element-91" data-mp="oaijculgu4pm">
        <div className={`sticky top-0 z-10 px-5 md:px-8 py-4 backdrop-blur-xl border-b shadow-sm ${isLightTheme ? 'bg-white/80 border-black/[0.06]' : 'bg-[#1A1A1A]/80 border-white/[0.06]'}`} data-id="element-92" data-mp="ocl717z5blr6">
          <button onClick={() => setSelectedProject(null)} className={`flex items-center gap-2 font-mono text-xs uppercase tracking-wider transition-colors font-medium ${isLightTheme ? 'text-[#6B6B6B] hover:text-[#1A1A1A]' : 'text-[#9B9B9B] hover:text-[#E8E8E6]'}`} data-id="element-93" data-mp="eicievd8kcya">
            <ArrowLeft className="w-4 h-4" data-id="element-94" data-mp="nabq60kejkf" />
            {t('work.back')}
          </button>
        </div>

        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="max-w-4xl mx-auto w-full p-5 md:p-16 space-y-12 md:space-y-16" data-id="element-95" data-mp="3ms4bi5j6hvun">
          <motion.div variants={itemVariants} className="space-y-8" data-id="element-96" data-mp="1l10yxapqa77g">
            <div className={`aspect-video rounded-2xl overflow-hidden relative shadow-lg ${isLightTheme ? 'bg-black/5' : 'bg-white/5'}`} data-id="element-97" data-mp="1kyog6vc5bstc">
              {selectedProject.videoUrl ? selectedProject.videoUrl.endsWith('.mp4') ? <video src={selectedProject.videoUrl} className="w-full h-full object-cover" autoPlay muted loop controls data-id="element-98" data-mp="22alp3r30o9kt" /> : <iframe src={selectedProject.videoUrl} className="w-full h-full" allow="autoplay" title={selectedProject.title} data-id="element-99" data-mp="1elaivu2wdmza" /> : <img src={selectedProject.heroImage} alt={selectedProject.title} className="w-full h-full object-cover" data-id="element-100" data-mp="2uz0y9wzgvxo8" />}
            </div>

            <div className="space-y-5" data-id="element-101" data-mp="2xk8gei0e8qqq">
              <h1 className={`text-3xl sm:text-4xl md:text-6xl font-bold tracking-tight ${isLightTheme ? 'text-[#1A1A1A]' : 'text-[#E8E8E6]'}`} data-id="element-102" data-mp="2xl9t0qx25lak">
                {selectedProject.title}
              </h1>
              <p className={`text-xl sm:text-2xl font-medium ${isLightTheme ? 'text-[#6B6B6B]' : 'text-[#9B9B9B]'}`} data-id="element-103" data-mp="32hqs2g5spk2f">
                {selectedProject.subtitle}
              </p>
              <div className="flex flex-wrap gap-2 pt-3" data-id="element-104" data-mp="3ch9ags624xsd">
                {selectedProject.tags.map(tag => <span key={tag} className={`px-3 py-1.5 rounded-md font-mono text-xs ${isLightTheme ? 'bg-black/5 text-[#1A1A1A]' : 'bg-white/10 text-[#E8E8E6]'}`} data-id="element-105" data-mp="3q6t6zg91oyt8">
                    {tag}
                  </span>)}
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-16" data-id="element-106" data-mp="ind59pepn41g">
            <div className="md:col-span-2 space-y-12 md:space-y-16" data-id="element-107" data-mp="il8sehyu2up1">
              <motion.section variants={itemVariants} className="space-y-5" data-id="element-108" data-mp="mnnbn36ezwgu">
                <h3 className="font-mono text-xs uppercase tracking-widest text-[#C8956C]" data-id="element-109" data-mp="8pyp7qwp8h9o">
                  {t('work.intention')}
                </h3>
                <p className={`text-lg md:text-xl leading-relaxed ${isLightTheme ? 'text-[#1A1A1A]' : 'text-[#E8E8E6]'}`} data-id="element-110" data-mp="dqcxna27pt88">
                  {projectStory.intentie.description}
                </p>
              </motion.section>

              <motion.section variants={itemVariants} className="space-y-5" data-id="element-111" data-mp="3m54vozd77fxj">
                <h3 className="font-mono text-xs uppercase tracking-widest text-[#C8956C]" data-id="element-112" data-mp="3r2n0qeo3hb09">
                  {t('work.process')}
                </h3>
                <p className={`text-lg md:text-xl leading-relaxed ${isLightTheme ? 'text-[#1A1A1A]' : 'text-[#E8E8E6]'}`} data-id="element-113" data-mp="3c7z1vjd79atm">
                  {projectStory.proces.description}
                </p>
              </motion.section>

              <motion.section variants={itemVariants} className="space-y-5" data-id="element-114" data-mp="2t8m4991xp8qc">
                <h3 className="font-mono text-xs uppercase tracking-widest text-[#C8956C]" data-id="element-115" data-mp="2oa1397euyl0u">
                  {t('work.outcome')}
                </h3>
                <p className={`text-lg md:text-xl leading-relaxed ${isLightTheme ? 'text-[#1A1A1A]' : 'text-[#E8E8E6]'}`} data-id="element-116" data-mp="2jdap4pmm5zi2">
                  {projectStory.resultaat.description}
                </p>
              </motion.section>
            </div>

            <motion.div variants={itemVariants} className="space-y-10" data-id="element-117" data-mp="36zqvpnv5eupl">
              {projectStory.proces.tools && <div className="space-y-5" data-id="element-118" data-mp="321pgje0uffje">
                  <h3 className="font-mono text-xs uppercase tracking-widest text-[#C8956C]" data-id="element-119" data-mp="1vmep55807xzq">
                    {t('work.tech')}
                  </h3>
                  <div className="flex flex-wrap gap-2" data-id="element-120" data-mp="1qlhb94u11q41">
                    {projectStory.proces.tools.map(tool => <span key={tool} className={`px-3 py-1.5 rounded-md font-mono text-xs border ${isLightTheme ? 'border-black/[0.06] text-[#6B6B6B]' : 'border-white/[0.06] text-[#9B9B9B]'}`} data-id="element-121" data-mp="b3uflnxjxyrt">
                        {tool}
                      </span>)}
                  </div>
                </div>}

              {projectStory.resultaat.metrics && <div className="space-y-5" data-id="element-122" data-mp="tsrb1v4oqxbm">
                  <h3 className="font-mono text-xs uppercase tracking-widest text-[#C8956C]" data-id="element-123" data-mp="ytoq3yuefb8w">
                    {t('work.results')}
                  </h3>
                  <ul className="space-y-4" data-id="element-124" data-mp="13s032ifzq1vc">
                    {projectStory.resultaat.metrics.map(metric => <li key={metric} className={`flex items-start gap-3 text-base ${isLightTheme ? 'text-[#1A1A1A]' : 'text-[#E8E8E6]'}`} data-id="element-125" data-mp="30mwd7ug96x5c">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#C8956C] mt-2 flex-shrink-0" data-id="element-126" data-mp="16cy11wijzi02" />
                        <span data-id="element-127" data-mp="1l1ugwfqupzb8">{metric}</span>
                      </li>)}
                  </ul>
                </div>}
            </motion.div>
          </div>

          {selectedProject.gallery && selectedProject.gallery.length > 0 && <motion.div variants={itemVariants} className="pt-8 space-y-8" data-id="element-128" data-mp="sdf3tyra5r2j">
              <h3 className="font-mono text-xs uppercase tracking-widest text-[#C8956C]" data-id="element-129" data-mp="nfdljg1ohvs4">
                {t('work.gallery')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-130" data-mp="12903vrcsgh9n">
                {selectedProject.gallery.map((img, idx) => <div key={idx} className={`rounded-xl overflow-hidden border shadow-sm ${isLightTheme ? 'border-black/[0.06]' : 'border-white/[0.06]'}`} data-id="element-131" data-mp="2dpqahbl4jb3s" data-mpi={idx}>
                    <img src={img} alt={`Gallery image ${idx + 1}`} className="w-full h-auto object-cover" data-id="element-132" data-mp="2ga66dcecfiso" />
                  </div>)}
              </div>
            </motion.div>}

          {/* Call to Action */}
          <motion.div variants={itemVariants} className={`mt-16 p-8 md:p-12 rounded-2xl border text-center space-y-6 ${isLightTheme ? 'bg-black/[0.02] border-black/[0.06]' : 'bg-white/[0.02] border-white/[0.06]'}`} data-id="element-133" data-mp="2bc4nosktxlhq">
            <h3 className={`text-2xl md:text-3xl font-bold ${isLightTheme ? 'text-[#1A1A1A]' : 'text-[#E8E8E6]'}`} data-id="element-134" data-mp="2q9n85iqh5cjd">
              {t('work.cta_title')}
            </h3>
            <button onClick={() => openWindow('contact', t('contact.title'))} className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-[#C8956C] text-white font-medium hover:bg-[#B5825A] transition-colors shadow-lg shadow-[#C8956C]/20" data-id="element-135" data-mp="3hzrqlbuobphy">
              <MessageSquare className="w-5 h-5" data-id="element-136" data-mp="2hi8ewarr6k4z" />
              {t('work.cta_button')}
            </button>
          </motion.div>
        </motion.div>
      </div>;
  }
  return <div ref={scrollContainerRef} className="h-full overflow-y-auto p-5 md:p-16 pb-28" data-id="element-137" data-mp="22nlpn16kn3o3">
      <motion.div variants={containerVariants} initial="hidden" animate="visible" className="max-w-5xl mx-auto space-y-10 md:space-y-16" data-id="element-138" data-mp="1tsxn3isds02z">
        <motion.div variants={itemVariants} className="space-y-8" data-id="element-139" data-mp="1ylqrg5jxt6ej">
          <h1 className={`text-4xl md:text-6xl font-bold tracking-tight ${isLightTheme ? 'text-[#1A1A1A]' : 'text-[#E8E8E6]'}`} data-id="element-140" data-mp="1jw2pr37ug5qa">
            {t('work.heading')}
          </h1>

          <div className="flex flex-wrap gap-2.5" data-id="element-141" data-mp="1avcy8bpm91id">
            {filters.map(filter => <button key={filter.id} onClick={() => setActiveFilter(filter.id)} className={`px-5 py-2.5 rounded-lg font-mono text-xs transition-all ${activeFilter === filter.id ? 'bg-[#C8956C] text-white shadow-md shadow-[#C8956C]/20 scale-105' : isLightTheme ? 'bg-black/5 text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-black/10' : 'bg-white/5 text-[#9B9B9B] hover:text-[#E8E8E6] hover:bg-white/10'}`} data-id="element-142" data-mp="1eulr7ecu0vcv">
                {filter.label}
              </button>)}
          </div>
        </motion.div>

        <div className="space-y-10" data-id="element-143" data-mp="1a7du9l2jcfy8">
          {/* Flagship */}
          {flagship && <motion.div onClick={() => setSelectedProject(flagship)} whileHover={{
          y: -4,
          scale: 1.01
        }} transition={{
          type: 'spring',
          stiffness: 400,
          damping: 30
        }} className={`group cursor-pointer rounded-2xl overflow-hidden border transition-all duration-500 hover:shadow-2xl ${isLightTheme ? 'bg-white/90 backdrop-blur-sm border-black/[0.06] hover:border-black/15 shadow-black/[0.02] hover:shadow-black/[0.08]' : 'bg-[#1A1A1A]/90 backdrop-blur-sm border-white/[0.06] hover:border-white/15 shadow-black/20 hover:shadow-black/40'}`} data-id="element-144" data-mp="1nyanne4ad16c">
              <div className={`aspect-video md:aspect-[21/9] overflow-hidden relative ${isLightTheme ? 'bg-black/[0.02]' : 'bg-white/[0.02]'}`} data-id="element-145" data-mp="14c5n97xhpxp6">
                <div className={`absolute inset-0 z-10 pointer-events-none ring-1 ring-inset ${isLightTheme ? 'ring-black/[0.08]' : 'ring-white/[0.08]'}`} data-id="element-146" data-mp="1e9s0zr31zj7k" />
                <img src={flagship.thumbnail} alt={flagship.title} className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" data-id="element-147" data-mp="1i9kgyous0rmy" />
              </div>
              <div className="p-6 md:p-10 space-y-4" data-id="element-148" data-mp="3p2d46y7jerxp">
                <h2 className={`text-2xl md:text-4xl font-bold ${isLightTheme ? 'text-[#1A1A1A]' : 'text-[#E8E8E6]'}`} data-id="element-149" data-mp="3hi28g4ckekbm">
                  {flagship.title}
                </h2>
                <p className={`text-lg md:text-xl font-medium ${isLightTheme ? 'text-[#6B6B6B]' : 'text-[#9B9B9B]'}`} data-id="element-150" data-mp="37rzcpwe26s9l">
                  {flagship.subtitle}
                </p>
                <div className="pt-4 flex flex-wrap gap-2" data-id="element-151" data-mp="4aqzxqy6uilq">
                  {flagship.tags.slice(0, 4).map(tag => <span key={tag} className={`px-3 py-1.5 rounded-md font-mono text-xs ${isLightTheme ? 'bg-black/5 text-[#6B6B6B]' : 'bg-white/5 text-[#9B9B9B]'}`} data-id="element-152" data-mp="3vimoisr3i21f">
                      {tag}
                    </span>)}
                </div>
              </div>
            </motion.div>}

          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10" data-id="element-153" data-mp="2b0yiz4n6hqvk">
            {restProjects.map(project => <motion.div key={project.id} onClick={() => setSelectedProject(project)} whileHover={{
            y: -4,
            scale: 1.01
          }} transition={{
            type: 'spring',
            stiffness: 400,
            damping: 30
          }} className={`group cursor-pointer rounded-2xl overflow-hidden border transition-all duration-500 hover:shadow-2xl ${isLightTheme ? 'bg-white/90 backdrop-blur-sm border-black/[0.06] hover:border-black/15 shadow-black/[0.02] hover:shadow-black/[0.08]' : 'bg-[#1A1A1A]/90 backdrop-blur-sm border-white/[0.06] hover:border-white/15 shadow-black/20 hover:shadow-black/40'}`} data-id="element-154" data-mp="2rqtt7zuuhk7">
                <div className={`aspect-video overflow-hidden relative ${isLightTheme ? 'bg-black/[0.02]' : 'bg-white/[0.02]'}`} data-id="element-155" data-mp="2x8aq8c2v0es0">
                  <div className={`absolute inset-0 z-10 pointer-events-none ring-1 ring-inset ${isLightTheme ? 'ring-black/[0.08]' : 'ring-white/[0.08]'}`} data-id="element-156" data-mp="2tqn2askye2or" />
                  <img src={project.thumbnail} alt={project.title} className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" data-id="element-157" data-mp="33q43awp9fsee" />
                </div>
                <div className="p-6 md:p-8 space-y-3" data-id="element-158" data-mp="262c7a61xux8e">
                  <h3 className={`text-xl md:text-2xl font-bold ${isLightTheme ? 'text-[#1A1A1A]' : 'text-[#E8E8E6]'}`} data-id="element-159" data-mp="263n7xx037ova">
                    {project.title}
                  </h3>
                  <p className={`text-base md:text-lg line-clamp-2 ${isLightTheme ? 'text-[#6B6B6B]' : 'text-[#9B9B9B]'}`} data-id="element-160" data-mp="2fy6xpncelwlq">
                    {project.subtitle}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </motion.div>
    </div>;
}